import { ImageGalleryEnrichedImage, ImageGalleryRawImage, ImageShape } from '../../ImageGallery.types';
import { DEFAULT_IMAGE_RATIOS, DEFAULT_LARGE_IMAGE_PIXEL_THRESHOLD } from '../ImageGalleryGrid.constants';
import { getClosestImageShape } from './getClosestImageShape';

/**
 * Enriches a raw image object with additional properties derived from its dimensions.
 *
 * Calculates aspect ratio, pixel area, shape classification, and whether the image
 * is considered "large" based on a pixel area threshold.
 *
 * @param image - The raw image object containing at least width and height
 * @param originalIndex - Index of the image in the original input array
 * @param imageRatios - Optional map of ImageShape to target aspect ratios (defaults to DEFAULT_IMAGE_RATIOS)
 * @param largeImagePixelThreshold - Optional pixel area threshold to determine if image is large (defaults to DEFAULT_LARGE_IMAGE_PIXEL_THRESHOLD)
 * @returns The enriched image with added properties: shape, aspectRatio, pixelArea, and isLarge
 */
export function enrichRawImage(
    image: ImageGalleryRawImage,
    originalIndex: number,
    imageRatios = DEFAULT_IMAGE_RATIOS,
    largeImagePixelThreshold = DEFAULT_LARGE_IMAGE_PIXEL_THRESHOLD
): ImageGalleryEnrichedImage {
    const { width, height } = image;
    const aspectRatio = width / height;
    const pixelArea = width * height;

    const shape: ImageShape = getClosestImageShape(aspectRatio, imageRatios);

    return {
        ...image,
        shape,
        aspectRatio,
        pixelArea,
        isLarge: pixelArea >= largeImagePixelThreshold,
        originalIndex,
    };
}
